/**
 * UsageIndicator - Shows token usage in the top bar with hover details
 */

import React, { useState } from 'react';
import { Zap, AlertTriangle } from 'lucide-react';
import { useUsage } from '../../hooks/useUsage';

export function UsageIndicator() {
  const { data, loading, isNearLimit, isOverLimit } = useUsage();
  const [showTooltip, setShowTooltip] = useState(false);

  if (loading || !data) {
    return null;
  }

  const dailyPercent = Math.min(data.percentages.daily_tokens, 100);
  const monthlyPercent = Math.min(data.percentages.monthly_tokens, 100);

  // Determine color based on usage
  const getColor = () => {
    if (isOverLimit) return 'text-red-400';
    if (isNearLimit) return 'text-warning-400';
    return 'text-text-muted';
  };

  const getBgColor = () => {
    if (isOverLimit) return 'bg-red-500';
    if (isNearLimit) return 'bg-warning-500';
    return 'bg-primary-500';
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(0)}K`;
    return tokens.toString();
  };

  const dailyUsed = data.limits.daily_tokens - data.remaining.daily_tokens;
  const monthlyUsed = data.limits.monthly_tokens - data.remaining.monthly_tokens;
  const dailyRequests = data.limits.daily_requests - data.remaining.daily_requests;

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-surface-800 border border-surface-600 cursor-default">
        {isOverLimit ? (
          <AlertTriangle className="w-4 h-4 text-red-400" />
        ) : (
          <Zap className={`w-4 h-4 ${getColor()}`} />
        )}

        <div className="flex items-center gap-2">
          {/* Progress bar */}
          <div className="w-16 h-1.5 bg-surface-600 rounded-full overflow-hidden">
            <div
              className={`h-full ${getBgColor()} transition-all duration-300`}
              style={{ width: `${dailyPercent}%` }}
            />
          </div>
          <span className={`text-xs ${getColor()} whitespace-nowrap`}>
            {formatTokens(dailyUsed)} / {formatTokens(data.limits.daily_tokens)}
          </span>
        </div>
      </div>

      {/* Hover Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 z-50">
          <div className="bg-surface-900 border border-surface-600 rounded-lg shadow-xl p-3 min-w-[220px]">
            <div className="text-sm font-medium text-text-primary mb-3">Usage Details</div>

            {/* Daily Tokens */}
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-text-muted">Daily tokens</span>
                <span className="text-text-secondary">
                  {formatTokens(dailyUsed)} / {formatTokens(data.limits.daily_tokens)}
                </span>
              </div>
              <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${dailyPercent >= 100 ? 'bg-red-500' : dailyPercent >= 80 ? 'bg-warning-500' : 'bg-primary-500'} transition-all duration-300`}
                  style={{ width: `${dailyPercent}%` }}
                />
              </div>
            </div>

            {/* Monthly Tokens */}
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-text-muted">Monthly tokens</span>
                <span className="text-text-secondary">
                  {formatTokens(monthlyUsed)} / {formatTokens(data.limits.monthly_tokens)}
                </span>
              </div>
              <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${monthlyPercent >= 100 ? 'bg-red-500' : monthlyPercent >= 80 ? 'bg-warning-500' : 'bg-primary-500'} transition-all duration-300`}
                  style={{ width: `${monthlyPercent}%` }}
                />
              </div>
            </div>

            {/* Daily Requests */}
            <div className="flex justify-between text-xs">
              <span className="text-text-muted">Requests today</span>
              <span className="text-text-secondary">
                {dailyRequests} / {data.limits.daily_requests}
              </span>
            </div>

            {/* Warning message */}
            {isOverLimit && (
              <div className="mt-3 pt-2 border-t border-surface-700">
                <p className="text-xs text-red-400">Limit reached. Usage resets at midnight UTC.</p>
              </div>
            )}
            {isNearLimit && !isOverLimit && (
              <div className="mt-3 pt-2 border-t border-surface-700">
                <p className="text-xs text-warning-400">Approaching usage limit.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
